import React, { FC } from 'react';
import { Map } from 'immutable';

import { ImmutableTrip } from 'src/types/trips/Trips';
import type { DropdownOption } from 'src/common/components/DropdownDS22';
import {
  PATHS,
} from '../../../../common/constants';
import { preserveSearch } from '../../../../common/utils/routing';
import { TRIP_STATES, INQUIRY_CANCELLED_STATES } from '../../../constants';
import {
  CaptainCard,
  CommentCard,
  DatesCard,
  DurationCard,
  GroupSizeCard,
  TimeCard,
  VesselCard,
  DirectBookingCard,
} from '../../cards';
import TripDetailsTitle from '../../presentation/TripDetailsTitle';
import TripMenu from '../../TripMenu';
import TripPanel from '../../presentation/TripPanel';
import Card from '../../presentation/Card';
import TripDetailComment from '../../TripDetailComment';
import content, { getMessage } from '../content';
import CTA, { CTAExternalLink } from '../../CTA';
import ArchiveButton from '../../ArchiveButton';

type Props = {
  cancellationDropdownOptions?: DropdownOption[];
  trip: ImmutableTrip;
  location: Location;
};

const Cancelled: FC<Props> = ({
  cancellationDropdownOptions,
  trip,
  location,
}) => {
  const tripState = trip.getIn(['state', 'state']);
  const urlParams = new URLSearchParams({
    // Opens trip cart's bottom sheet on mobile.
    sheet: 'true',
  });
  const listingUrl = `${trip.getIn(['boat', 'listing_url'])}?${urlParams.toString()}`;
  const archived = trip.get('archived');
  const isDirectBooking = trip.get('direct_booking', false);
  const cta = (
    <CTA classNameModifier="withSidebar">
      {tripState === TRIP_STATES.RENTER_INQUIRY_CANCELLED ? (
        <>
          <TripMenu
            cancellationDropdownOptions={cancellationDropdownOptions}
            trip={trip}
          />
          <CTAExternalLink href={listingUrl}>
            Send a new inquiry
          </CTAExternalLink>
        </>
      ) : (
        <ArchiveButton
          archived={archived}
          trip={trip}
        >
          {`${archived ? 'Restore' : 'Archive'} this Inquiry`}
        </ArchiveButton>
      )}
    </CTA>
  );

  const inquiryCancelMessage = trip.get('inquiry_cancel_message') || Map();
  const inquiryCancelMessageContent = inquiryCancelMessage.get('content');
  const showInquiryCancelMessage = inquiryCancelMessageContent
    && INQUIRY_CANCELLED_STATES.includes(tripState);

  return (
    <TripPanel
      trip={trip}
      subheader={content.title[tripState]}
      intro={getMessage(trip)}
      cta={cta}
      backLocation={preserveSearch(PATHS.INBOX, location)}
      classNameModifier="withoutSideMargins "
    >
      {showInquiryCancelMessage && (
        <Card
          withDashedBorderBottom
        >
          <TripDetailComment
            user={trip.get('renter')}
          >
            {inquiryCancelMessageContent}
          </TripDetailComment>
        </Card>
      )}
      {isDirectBooking && <DirectBookingCard trip={trip} />}
      <TripDetailsTitle />
      <VesselCard
        readOnly
        trip={trip}
      />
      <DatesCard
        readOnly
        trip={trip}
      />
      <TimeCard
        readOnly
        trip={trip}
      />
      <DurationCard
        trip={trip}
      />
      <GroupSizeCard trip={trip} readOnly />
      <CaptainCard trip={trip} readOnly />
      <CommentCard trip={trip} readOnly />
    </TripPanel>
  );
};

export default Cancelled;
